import { useState, useCallback, useEffect } from 'react';
import { showToast } from '../components/atoms/Toast/Toast';
import authService from '../services/authService';
import { extractErrorDetail, translateSupervisionError } from '../utils/supervisionErrors';
const arraysEqualById = (a, b) => {
  if (a.length !== b.length) return false;
  const sa = [...a].map(String).sort();
  const sb = [...b].map(String).sort();
  return sa.every((v, i) => v === sb[i]);
};

export const useUsers = ({ isAuthenticated, accessToken, currentUser, onlineStatusRef }) => {

  const [users,        setUsers]        = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [adminsList,   setAdminsList]   = useState([]);

  // ─── Fetch ─────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    if (!isAuthenticated || !accessToken) return;
    setLoadingUsers(true);
    try {
      const data = await authService.getUsers();
      setUsers(data.map((u) => ({
        ...u,
        is_online: onlineStatusRef.current[u.id] ?? u.is_online ?? false,
      })));

      if (currentUser?.role === 'superadmin') {
        setAdminsList(await authService.getAdmins());
      } else if (currentUser?.role === 'admin') {
        try {
          setAdminsList((await authService.getSupervisedAdmins()) || []);
        } catch {
          setAdminsList([]);
        }
      }
    } catch (e) {
      showToast(e.message || 'Erreur chargement', 'error');
    } finally {
      setLoadingUsers(false);
    }
  }, [isAuthenticated, accessToken, currentUser, onlineStatusRef]);

  useEffect(() => {
    if (isAuthenticated && accessToken) fetchUsers();
  }, [isAuthenticated, accessToken]); // eslint-disable-line

  // ─── Helpers privés ────────────────────────────────────────────────
  const syncSupervisors = async (adminId, oldIds = [], newIds = []) => {
    const oldSet   = new Set(oldIds.map(String));
    const newSet   = new Set(newIds.map(String));
    const toAdd    = [...newSet].filter((id) => !oldSet.has(id));
    const toRemove = [...oldSet].filter((id) => !newSet.has(id));
    await Promise.all([
      ...toAdd.map((id)    => authService.assignSupervisor(adminId, id)),
      ...toRemove.map((id) => authService.unassignSupervisor(adminId, id)),
    ]);
  };

  // ─── Handlers ──────────────────────────────────────────────────────
const handleCreateUser = async (userData) => {
  const {
    supervised_by_admin_ids = [],
    admin_id, confirmPassword,
    active, role, ...rest
  } = userData;

  let response;

  if (currentUser.role === 'superadmin') {
    response = role === 'admin'
      ? await authService.createAdmin(
          { ...rest, role, active },        // ✅ role réintégré
          supervised_by_admin_ids.map(Number).filter(Boolean)
        )
      : await authService.createUser(
          { ...rest, role },                // ✅ role réintégré
          supervised_by_admin_ids[0] || admin_id || null
        );
  } else if (currentUser.role === 'admin') {
    response = role === 'admin'
      ? await authService.createAdmin({ ...rest, role, active })
      : await authService.createUser({ ...rest, role });
  }

  return response;
};
const handleUpdateUser = async (userId, userData, confirm = false) => {
  if (confirm) {
    const response = await authService.updateUser(userId, {}, true);
    showToast('Utilisateur modifié', 'success');
    await fetchUsers();
    return response;
  }

  const { supervised_by_admin_ids, supervised_by, ...dataToUpdate } = userData;
  const targetUser = users.find((u) => String(u.id) === String(userId));
  const role       = userData.role || targetUser?.role;

  try {
    let response;
    if (role === 'user') {
      response = await authService.updateUser(userId, {
        ...dataToUpdate,
        supervisor_ids: (supervised_by_admin_ids || []).map(Number).filter(Boolean),
      });
    } else {
      response = await authService.updateUser(userId, dataToUpdate);
      const oldIds = (targetUser?.supervisor_ids || []).map(String);
      const newIds = (supervised_by_admin_ids    || []).map(String);
      if (!arraysEqualById(oldIds, newIds)) await syncSupervisors(userId, oldIds, newIds);
    }

    if (response?.data?.preview) {
      showToast('Utilisateur modifié', 'success');
      await fetchUsers();
    }
    return response;
  } catch (e) {
    const detail = extractErrorDetail(e);
    const supervisionMsg = translateSupervisionError(detail);
    throw new Error(supervisionMsg || detail || e.message);
  }
};


  const handleDeleteUser = async (userId) => {
    try {
      const response = await authService.deleteUser(userId);
      await fetchUsers();
      return response;
    } catch (e) {
      const detail = extractErrorDetail(e);
      const supervisionMsg = translateSupervisionError(detail);
 
      showToast(supervisionMsg || detail || 'Erreur suppression', 'error');
 
      // pas de throw : UsersTable.handleConfirm n'a pas de try/catch,
      // le ConfirmBar se referme normalement et le toast porte l'explication.
      return null;
    }
  };
  // ── NOUVEAU : toggle statut actif/inactif ──────────────────────────
const handleToggleStatus = async (userId, active) => {
    try {
        const response = await authService.updateUserStatus(userId, active ? 'true' : 'false');
        setUsers((prev) => prev.map((u) =>
            String(u.id) === String(userId) ? { ...u, active } : u
        ));
        return response; // ← indispensable
    } catch (e) {
        showToast(e.message || 'Erreur changement de statut', 'error');
        throw e;
    }
};
const handleSupervisionAdd = async (supervisorIdOrIds, targetUserId) => {
  try {
    const targetUser = users.find((u) => String(u.id) === String(targetUserId));
 
    if (targetUser?.role === 'user') {
      // replace complet : anciens + nouveau
      const currentIds = (targetUser.supervisor_ids || []).map(String);
      const newId = String(supervisorIdOrIds);
      const mergedIds = [...new Set([...currentIds, newId])].map(Number);
      await authService.assignSupervisor(targetUserId, mergedIds);
    } else {
      // admin → query param classique
      await authService.assignSupervisor(targetUserId, supervisorIdOrIds);
    }
 
    showToast('Superviseur(s) mis à jour', 'success');
    await fetchUsers();
  } catch (e) {
    showToast(e.message || "Erreur assignation", 'error');
  }
};

const handleSupervisionRemove = async (supervisorIdOrIds, targetUserId) => {
  try {
    const targetUser = users.find((u) => String(u.id) === String(targetUserId));
 
    if (targetUser?.role === 'user') {
      // replace complet : anciens sans celui retiré
      const currentIds = (targetUser.supervisor_ids || []).map(String);
      const removeId = String(supervisorIdOrIds);
      const mergedIds = currentIds.filter((id) => id !== removeId).map(Number);
      if (mergedIds.length === 0) {
        showToast('Un utilisateur doit avoir au moins un superviseur', 'error');
        return;
      }
      await authService.assignSupervisor(targetUserId, mergedIds);
    } else {
      await authService.unassignSupervisor(targetUserId, supervisorIdOrIds);
    }
 
    showToast('Superviseur(s) mis à jour', 'success');
    await fetchUsers();
  } catch (e) {
    showToast(e.message || 'Erreur retrait', 'error');
  }
};

const handleSupervisionReplace = async (oldSupervisorId, newSupervisorId, targetUserId) => {
  try {
    const targetUser = users.find((u) => String(u.id) === String(targetUserId));

    if (targetUser?.role === 'user') {
      const currentIds = (targetUser.supervisor_ids || []).map(String);
      const finalIds = currentIds
        .filter((id) => id !== String(oldSupervisorId))
        .concat(String(newSupervisorId));
      const dedupedIds = [...new Set(finalIds)].map(Number);

      await authService.assignSupervisor(targetUserId, dedupedIds);
    } else {
      // admin → un seul superviseur à la fois, remplacement direct
      await authService.assignSupervisor(targetUserId, newSupervisorId);
    }

    showToast('Superviseur(s) mis à jour', 'success');
    await fetchUsers();
  } catch (e) {
    const detail = extractErrorDetail(e);
    const supervisionMsg = translateSupervisionError(detail);
    showToast(supervisionMsg || detail || 'Erreur remplacement', 'error');
  }
};
const handleSupervisionReplaceAll = async (userId, newIds) => {
  try {
    const targetUser = users.find((u) => String(u.id) === String(userId));

    if (targetUser?.role === 'admin') {
      const singleId = newIds[0];
      if (!singleId) {
        throw new Error('Veuillez sélectionner un superviseur.');
      }
      await authService.assignSupervisor(userId, Number(singleId));
    } else {
      await authService.assignSupervisor(userId, newIds.map(Number));
    }

    showToast('Superviseur(s) mis à jour', 'success'); // ✅ toast de succès conservé ici (un seul endroit le fait)
    await fetchUsers();
  } catch (e) {
    const detail = extractErrorDetail(e);
    const supervisionMsg = translateSupervisionError(detail);
    // ❌ plus de showToast ici — on laisse le composant appelant l'afficher
    throw new Error(supervisionMsg || detail || e.message);
  }
};
  const handleShareHierarchy = async (sourceAdminId, formData, sourceAdmin) => {
    try {
      const EXCLUDED = new Set([
        'id','name','lastname','username','email',
        'password','role','is_active','created_at','updated_at',
      ]);
      const inherited = Object.fromEntries(
        Object.entries(sourceAdmin).filter(
          ([k, v]) => !EXCLUDED.has(k) && v != null && v !== ''
        )
      );
      await authService.shareHierarchy(sourceAdminId, {
        username: formData.username, email:    formData.email,
        name:     formData.name,     lastname: formData.lastname,
        password: formData.password, ...inherited,
      });
      showToast('Hiérarchie partagée', 'success');
      await fetchUsers();
    } catch (e) {
      showToast(e.message || 'Erreur partage', 'error');
      throw e;
    }
  };

  return {
    // State
    users,
    setUsers,
    loadingUsers,
    adminsList,
    // Fetch
    fetchUsers,
    // Handlers
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
    handleToggleStatus,
    handleSupervisionAdd,
    handleSupervisionRemove,
    handleSupervisionReplace,
    handleSupervisionReplaceAll,
    handleShareHierarchy,

  };
};